import webservice from './Service/webservice';
import { AesDecrypt, keyPromise } from './Service/crypto';

const api = {
    async fetch(url: string, options: RequestInit = {}): Promise<Response> {
        // The session is gone and the page is on its way to the login screen: don't hand
        // callers 401 bodies they would try to .map(), just let the navigation take over
        if (redirectingToLogin) return pendingForever();

        let token = localStorage.getItem("token");
        // console.log(`[api.fetch] URL: ${url}, Token: "${token}"`);

        const headers: any = {
            'Content-type': 'application/json; charset=UTF-8',
            'Authorization': `Bearer ${token}`,
            ...options.headers,
        };
        if (options.body instanceof FormData) {
            delete headers['Content-type'];
        }
        let response = await fetch(webservice + url, {
            ...options,
            headers,
        });
        if (response.status === 401) {
            const newAccessToken = await refreshAccessToken();
            if (newAccessToken) {
                const newHeaders = {
                    ...headers,
                    'Authorization': `Bearer ${newAccessToken}`,
                };

                response = await fetch(webservice + url, {
                    ...options,
                    headers: newHeaders,
                });
            }
        }

        if (response.status === 401 && token && await signOutIfSessionGone(response)) {
            return pendingForever();
        }

        return response;
    },
};

// Returns the new access token, or null when the refresh token is missing or no longer valid
async function refreshAccessToken(): Promise<string | null> {
    const refreshToken = localStorage.getItem("refresh_token");
    // Don't attempt refresh if there's no refresh token stored
    if (!refreshToken) return null;
    try {
        const resToken = await fetch(webservice + 'api/Authentication/refresh-token', {
            method: 'POST',
            body: JSON.stringify({ refreshToken: refreshToken }),
            headers: {
                'Content-type': 'application/json; charset=UTF-8',
            },
        });
        const resTokenData = await resToken.json();
        const key = await keyPromise;
        const resAccessTokenData = await AesDecrypt(key, resTokenData);
        const newAccessToken = resAccessTokenData?.accessToken;
        // Only update stored token if refresh actually returned a valid token
        if (!newAccessToken) return null;
        localStorage.setItem("token", newAccessToken);
        return newAccessToken;
    } catch {
        return null;
    }
}

const pendingForever = () => new Promise<Response>(() => { });

// Only one session per user. When the backend says this browser's session is over (signed in on
// another device, logged out there, or expired and not refreshable) sign out right away and tell
// the user why on the login screen. A blacklisted token (this browser's own logout in progress)
// is left to the logout flow.
let redirectingToLogin = false;
async function signOutIfSessionGone(response: Response): Promise<boolean> {
    if (redirectingToLogin) return true;
    let body: any = null;
    try {
        body = await response.clone().json();
    } catch {
        return false;
    }
    let reason = '';
    if (body?.code === 'SESSION_REPLACED') reason = 'session_replaced';
    else if (body?.code === 'SESSION_ENDED' || body?.error === 'Invalid or expired token') reason = 'session_expired';
    if (!reason) return false;

    redirectingToLogin = true;
    localStorage.clear();
    try {
        sessionStorage.setItem('logout_reason', reason);
    } catch { /* storage unavailable: the login screen just won't show the reason */ }
    window.dispatchEvent(new CustomEvent('auth_state_changed', { detail: { action: 'logout' } }));
    window.location.assign('/login');
    return true;
}

// Asks the backend whether this browser's session is still the active one; api.fetch signs
// out right away when it is not. Used to log a replaced device out without waiting for a click.
export async function checkSession() {
    if (!localStorage.getItem('token') || redirectingToLogin) return;
    try {
        await api.fetch('api/Authentication/session', { method: 'POST' });
    } catch {
        // offline or server unreachable: try again on the next check
    }
}

// Ends this browser's session on the server so the account can sign in elsewhere without the
// "already logged in" prompt. Never throws: logging out must not depend on it.
export async function endServerSession() {
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
        await fetch(webservice + 'api/Authentication/logout', {
            method: 'POST',
            headers: { 'Content-type': 'application/json; charset=UTF-8', 'Authorization': `Bearer ${token}` },
        });
    } catch (error) {
        console.error('Ending server session failed:', error);
    }
}

export default api;
